import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { appRouteEnums, menuTitleEnums } from "@enums";
import { StyledOverTimeView, StyledWrapCalendarComponent, OverTimeToggleModal } from ".";
import { TitleMenuComponent, CircularLoader, AppCustomeSelect, AppExportCsv, PassChangeButton } from "..";
import { useAppSelector, selectAuth, selectOvertimeLoading, selectOvertimeByMonth, selectEmployeesData, fetchOvertimes, selectGroupsData } from "@redux";
import { OT_STATUS_PENDING, OT_STATUS_REJECT } from "@configs";
import { OverTimeCalendar } from "./OverTimeCalendar";
import { useDispatch } from "react-redux";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useGroupSelectionHistories } from "src/Utils/Hooks/useGroupSelectionHistories";
import { overtimeSelectableTitles, overtimeTitles, useAppNavigation } from "@utils";

const defaultGroup = {
  id: 0,
  name: "全て",
};

// "People" option can be user or group
type PeopleOption = {
  value: string; // id of user or group as string
  label: string; // name of user or group
  type: "user" | "group";
};

// Currently, label is a Japanese string like "有給休暇", "夜勤", etc
// value is a number representing the overtime type
type SpanOption = {
  value: number;
  label: string;
};

export const OverTimeView = () => {
  // redux States
  const auth = useAppSelector(selectAuth);
  const id = auth?.userInfo?.id;

  // hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { navigateWithState, getQueryParam, navigatePreservingQuery } = useAppNavigation();

  const currentDate = new Date();
  const initialYear = currentDate.getFullYear();
  const initialMonth = String(currentDate.getMonth() + 1).padStart(2, '0'); // Ensure 2 digits
  const initialCalendarMonth = (`${initialYear}-${initialMonth}`);
  const initialCsvStartDate = new Date(Date.UTC(initialYear, currentDate.getMonth(), 1));
  const initialCsvEndDate = new Date(Date.UTC(initialYear, currentDate.getMonth() + 1, 0));

  // URLクエリパラメータから取得（フィルター情報をURLで管理）
  const monthFromQuery = getQueryParam("month");
  const userIdFromQuery = getQueryParam("userId");
  const groupIdFromQuery = getQueryParam("groupId");
  const statusFromQuery = getQueryParam("status");
  const fromQuery = getQueryParam("from");

  // component states
  // only userInputValue OR groupInputValue can be set
  const [userInputValue, setUserInputValue] = React.useState<PeopleOption | null>(null);
  const [groupInputValue, setGroupInputValue] = React.useState<PeopleOption | null>(null);
  const [peopleOptions, setPeopleOptions] = React.useState<PeopleOption[]>([]); // all user and groups in one list

  const [spanInputValue, setSpanInputValue] = React.useState<SpanOption | null>(null);
  const spanOptions = overtimeSelectableTitles;

  const [statusInputValue, setStatusInputValue] = React.useState<any>();

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [timeSheetDateData, setTimeSheetDateData] = React.useState<any>([]);
  const [overTimeIdData, setOverTimeIdData] = React.useState<any>();
  const [overTimeGroupIdData, setOverTimeGroupIdData] = React.useState<any>();
  const [date, setDate] = React.useState(new Date());
  const [dataListOT, setDataListOT] = React.useState<any>([]);
  const [calendarMonth, setCalendarMonth] = React.useState<string>(monthFromQuery || initialCalendarMonth);
  const [cvsStartDate, setCvsStartDate] = React.useState<string>(initialCsvStartDate.toISOString().split('T')[0]);
  const [cvsEndDate, setCvsEndDate] = React.useState<string>(initialCsvEndDate.toISOString().split('T')[0]);

  const [hasAutoSelected, setHasAutoSelected] = React.useState<boolean>(false);

  const statusOptions: { label: string; value: string }[] = [
    { label: "全て", value: "all" },
    { label: "未承認", value: "pending" },    // status 0
    { label: "承認済み", value: "approved" }, // status 1
    { label: "差し戻し", value: "rejected" }  // status 2
  ];

  // <AppExportCsv> key to force re-render when filter changes
  const filterKey = [
    cvsStartDate,
    cvsEndDate,
    spanInputValue?.value ?? "",
    spanInputValue?.label ?? "",
    userInputValue?.value ?? "",
    groupInputValue?.value ?? "",
    statusInputValue?.value ?? "",
  ].join("|");

  const overtimesLoading = useAppSelector(selectOvertimeLoading);
  const overtimeDataForMonth = useAppSelector(selectOvertimeByMonth(calendarMonth));
  const employeesData = useAppSelector(selectEmployeesData);
  const groupsData = useAppSelector(selectGroupsData);
  // const sortedUsers = useSortedUserList(users, true);

  const { list, fetchList, storeSelection } = useUserSelectionHistories(id ?? 0);
  const { groupList, fetchGroupList, storeGroupSelection } = useGroupSelectionHistories(id ?? 0);

  // URLクエリの月指定と内部状態の同期（URLが変わった時に月を追従）
  useEffect(() => {
    const currentMonthFromQuery = getQueryParam("month");
    if (currentMonthFromQuery && currentMonthFromQuery !== calendarMonth) {
      setCalendarMonth(currentMonthFromQuery);
    } else if (!currentMonthFromQuery && calendarMonth !== initialCalendarMonth) {
      setCalendarMonth(initialCalendarMonth);
    }
  }, [getQueryParam, calendarMonth, initialCalendarMonth]);

  useEffect(() => {
    if (!overtimeDataForMonth) {
      dispatch(fetchOvertimes({ month: calendarMonth, groups: [] }));
      return;
    }

    let filteredOvertimes = overtimeDataForMonth;

    const selectedSpan = (spanInputValue?.value) || null;
    const selectedUserId = userInputValue ? parseInt(userInputValue.value) : null;
    const selectedGroupId = groupInputValue ? parseInt(groupInputValue.value) : null;
    const selectedOTStatus = (statusInputValue?.value) || null;

    if (selectedOTStatus && selectedOTStatus !== "all") {
      filteredOvertimes = filterOvertimesByStatus(filteredOvertimes, selectedOTStatus);
    }

    if (selectedSpan) {
      filteredOvertimes = filterOvertimesBySpan(filteredOvertimes, selectedSpan);
    }

    if (selectedUserId) {
      filteredOvertimes = filterOvertimesByUser(filteredOvertimes, selectedUserId);
    }

    let groupFilteredOvertimeDataForMonth = [];

    if (!selectedGroupId) {
      groupFilteredOvertimeDataForMonth = filterOvertimesByGroup(filteredOvertimes);
    } else {
      groupFilteredOvertimeDataForMonth = filterOvertimesByGroup(filteredOvertimes, selectedGroupId);
    };

    fillOvertimesMultidays(groupFilteredOvertimeDataForMonth);
    setDataListOT(groupFilteredOvertimeDataForMonth);
  }, [calendarMonth, overtimeDataForMonth, spanInputValue?.value, userInputValue?.value, groupInputValue?.value, statusInputValue?.value, dispatch]);

  //ユーザーとグループのリアルタイム履歴反映のリスト読み込み
  useEffect(() => {
    if (list && groupList) {
      
      const userOptions: PeopleOption[] = list.map((user) => ({
        label: `${user.last_name} ${user.first_name}`,
        value: user.id.toString(),
        type: user.type,
      }));
      
      const currentUserOption = userOptions.find((user) => user.value === id?.toString());

      const userOptionsWithoutCurrentUser = userOptions.filter((user) => user.value !== id?.toString());
      
      const groupOptions = groupList.map((group) => ({
        label: group.name,
        value: group.id.toString(),
        type: group.type,
      }));

      const allPeopleOptions = [currentUserOption, ...userOptionsWithoutCurrentUser, ...groupOptions].filter((option) => option !== undefined) as PeopleOption[];

      setPeopleOptions(allPeopleOptions);

      // 初回アクセス時のみログインユーザー既存選択固定
      if (!hasAutoSelected && currentUserOption) {
        setUserInputValue(currentUserOption);
        setHasAutoSelected(true);
      }

    }
  }, [list, groupList]);

  // URLクエリからフィルター値を復元する
  // 理由: 直接URLを叩いた/共有された場合にも画面状態を同期させるため
  useEffect(() => {
    if (peopleOptions.length === 0) return;

    const currentUserIdFromQuery = getQueryParam("userId");
    const currentGroupIdFromQuery = getQueryParam("groupId");
    const currentStatusFromQuery = getQueryParam("status");

    // ユーザー/グループ選択の復元（userId優先）
    if (currentUserIdFromQuery === "all") {
      if (userInputValue || groupInputValue) {
        setUserInputValue(null);
        setGroupInputValue(null);
      }
    } else if (currentUserIdFromQuery) {
      const userOption = peopleOptions.find(opt => opt.type === "user" && opt.value === currentUserIdFromQuery);
      if (userOption && userOption.value !== userInputValue?.value) {
        setUserInputValue(userOption);
        setGroupInputValue(null);
      }
    } else if (currentGroupIdFromQuery) {
      const groupOption = peopleOptions.find(opt => opt.type === "group" && opt.value === currentGroupIdFromQuery);
      if (groupOption && groupOption.value !== groupInputValue?.value) {
        setGroupInputValue(groupOption);
        setUserInputValue(null);
      }
    }

    // ステータス選択の復元
    if (currentStatusFromQuery) {
      const statusOption = statusOptions.find(opt => opt.value === currentStatusFromQuery);
      if (statusOption && statusOption.value !== statusInputValue?.value) {
        setStatusInputValue(statusOption);
      }
    } else if (!currentStatusFromQuery && statusInputValue) {
      setStatusInputValue(null);
    }
  }, [peopleOptions, getQueryParam, userInputValue, groupInputValue, statusInputValue]);

  // 日報種別コンボ用：クエリから選択値を復元
  useEffect(() => {
    const currentOvertimeTypeFromQuery = getQueryParam("overtimeType");

    if (currentOvertimeTypeFromQuery) {
      const typeId = Number(currentOvertimeTypeFromQuery);
      if (!Number.isNaN(typeId)) {
        const typeOption = spanOptions.find((opt) => Number(opt.value) === typeId);
        if (typeOption && typeOption.value !== spanInputValue?.value) {
          setSpanInputValue(typeOption);
        }
      }
    } else if (!currentOvertimeTypeFromQuery && spanInputValue) {
      setSpanInputValue(null);
    }
  }, [getQueryParam, spanOptions, spanInputValue]);

  // events
  const handleOnChangeSpan = (value: SpanOption | null) => {
    if (!value) {
      setSpanInputValue(null);
      navigateWithState('/overtime', {
        month: calendarMonth,
        ...(userInputValue?.value && { userId: userInputValue.value }),
        ...(groupInputValue?.value && { groupId: groupInputValue.value }),
        ...(statusInputValue?.value && { status: statusInputValue.value }),
      }, 'push');
      return;
    }

    setSpanInputValue(value);
    navigateWithState('/overtime', {
      month: calendarMonth,
      ...(userInputValue?.value && { userId: userInputValue.value }),
      ...(groupInputValue?.value && { groupId: groupInputValue.value }),
      ...(statusInputValue?.value && { status: statusInputValue.value }),
      overtimeType: value.value,
    }, 'push');
  };

  const handleOnChangePeople = (value: PeopleOption) => {
    console.log("Change value", value);

    if (!value) {
      setUserInputValue(null);
      setGroupInputValue(null);

      // URLクエリパラメータで全員表示を明示（userId=all）、月とステータスは保持
      navigateWithState('/overtime', {
        month: calendarMonth,
        userId: 'all',
        ...(statusInputValue?.value && { status: statusInputValue.value }),
        ...(spanInputValue?.value !== undefined && spanInputValue?.value !== null && { overtimeType: spanInputValue.value }),
      }, 'push');
      return;
    }

    if (value.type === "user") {
      setGroupInputValue(null);
      setUserInputValue(value);

      //選択したユーザーの履歴保存
      storeSelection({ user_id: id, selected_user_id: value.value });
      
      // URLクエリパラメータを更新（月とステータスは保持）
      navigateWithState('/overtime', {
        month: calendarMonth,
        userId: value.value,
        ...(statusInputValue?.value && { status: statusInputValue.value }),
        ...(spanInputValue?.value !== undefined && spanInputValue?.value !== null && { overtimeType: spanInputValue.value }),
      }, 'push');
    }

    if (value.type === "group") {
      setUserInputValue(null);

      if (value.value === "0") {
        setGroupInputValue(null);

        // URLクエリパラメータを更新（月とステータスは保持）
        navigateWithState('/overtime', {
          month: calendarMonth,
          ...(statusInputValue?.value && { status: statusInputValue.value }),
          ...(spanInputValue?.value !== undefined && spanInputValue?.value !== null && { overtimeType: spanInputValue.value }),
        }, 'push');
        return;
      }
      setGroupInputValue(value);

      //選択したグループの履歴保存
      storeGroupSelection({ user_id: id, selected_group_id: value.value });
      
      // URLクエリパラメータを更新（月とステータスは保持）
      navigateWithState('/overtime', {
        month: calendarMonth,
        groupId: value.value,
        ...(statusInputValue?.value && { status: statusInputValue.value }),
        ...(spanInputValue?.value !== undefined && spanInputValue?.value !== null && { overtimeType: spanInputValue.value }),
      }, 'push');
    }

  };

  const handleOnChangeStatus = (value) => {
    if (!value) {
      setStatusInputValue(null);

      // URLクエリパラメータを更新（月とユーザー/グループは保持、statusは削除）
      navigateWithState('/overtime', {
        month: calendarMonth,
        ...(userInputValue?.value && { userId: userInputValue.value }),
        ...(groupInputValue?.value && { groupId: groupInputValue.value }),
        ...(spanInputValue?.value !== undefined && spanInputValue?.value !== null && { overtimeType: spanInputValue.value }),
      }, 'push');
      return;
    }

    setStatusInputValue(value);

    // URLクエリパラメータを更新（月とユーザー/グループは保持、statusを反映）
    navigateWithState('/overtime', {
      month: calendarMonth,
      ...(userInputValue?.value && { userId: userInputValue.value }),
      ...(groupInputValue?.value && { groupId: groupInputValue.value }),
      ...(spanInputValue?.value !== undefined && spanInputValue?.value !== null && { overtimeType: spanInputValue.value }),
      status: value.value,
    }, 'push');
  };

  const handleViewDetail = (values: { otId; userId; status }) => {
    if ((id === values.userId && values.status === OT_STATUS_PENDING) || (id === values.userId && values.status === OT_STATUS_REJECT)) {
      navigatePreservingQuery(`${appRouteEnums.PATH_OVERTIME}/edit/${values.otId}`);
    } else {
      navigatePreservingQuery(`${appRouteEnums.PATH_OVERTIME}/${values.otId}`);
    }
  };
  const handleCreateNewOT = () => {
    navigatePreservingQuery(`${appRouteEnums.PATH_OVERTIME}/new`);
  };
  // const handleSelectGroup = (id) => {
  //   setInputValue("");
  //   if (id === 0) {
  //     setSelectedStatus([]);
  //     return;
  //   }
  //   setSelectedStatus(checkExistUserSelected(id, selectedStatus) ? selectedStatus.filter((e) => e !== id) : [...selectedStatus, id]);
  // };

  const handleOpenModal = (date: any, id: number) => {
    setModalOpen(true);
    setTimeSheetDateData({
      date: date.startDate[0],
    });
    setOverTimeIdData({
      id,
    });
    // setOverTimeGroupIdData({ groupId });
  };
  const handleOpenModalId = (date: any, id: number, groupId: number) => {
    setModalOpen(true);
    setTimeSheetDateData({
      date: date.startDate,
    });
    setOverTimeIdData({
      id,
    });
    setOverTimeGroupIdData({ groupId });
  };

  const handleCloseToggle = () => {
    setModalOpen(false);
    setTimeSheetDateData([]);
    setOverTimeIdData("");
    setOverTimeGroupIdData("");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value + "-01"); // Ensure a valid Date format
    setDate(newDate);
  };

  /**
   * 月の変更を処理する共通関数（カレンダーと月選択の両方から使用）
   * @param newMonth - 新しい月（YYYY-MM形式）
   */
  const handleMonthChange = (newMonth: string) => {
    setCalendarMonth(newMonth);

    const [year, month] = newMonth.split('-').map(Number);

    const firstDay = new Date(Date.UTC(year, month - 1, 1));
    setCvsStartDate(firstDay.toISOString().split('T')[0]);

    const lastDay = new Date(Date.UTC(year, month, 0));
    setCvsEndDate(lastDay.toISOString().split('T')[0]);
    
    // URLクエリパラメータを更新（履歴に追加）
    // 月以外のフィルターも保持する
    navigateWithState('/overtime', {
      month: newMonth,
      ...(userInputValue?.value && { userId: userInputValue.value }),
      ...(groupInputValue?.value && { groupId: groupInputValue.value }),
      ...(statusInputValue?.value && { status: statusInputValue.value }),
      ...(spanInputValue?.value !== undefined && spanInputValue?.value !== null && { overtimeType: spanInputValue.value }),
    }, 'push');
  };

  /**
   * 月選択の変更を処理する
   * @param e - 月選択の入力イベント
   */
  const handleCalendarMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedMonth = e.target.value;
    handleMonthChange(selectedMonth);
  };

  const filterOvertimesBySpan = (overtimes, selectedSpan: number | null) => {
    console.log("filter by span", selectedSpan);
    console.log("before filter", overtimes);
    if (selectedSpan === null) return overtimes;
    const spanMatch = overtimeTitles.find(t => t.value === selectedSpan);
    if (!spanMatch) return overtimes;
    const spanTitleText = spanMatch.label;

    // Build a new array immutably
    const filteredOvertimeDays = overtimes.map((day) => {
      const originalUsers = Array.isArray(day.users) ? day.users : [];

      const filteredUsers = originalUsers
        .map((user) => {
          const originalOTS = Array.isArray(user.overtimes) ? user.overtimes : [];
          const filteredOTS = originalOTS.filter(
            (ot) => typeof ot?.title === "string" && ot.title === spanTitleText
          );
          return { ...user, overtimes: filteredOTS };
        })
        // Drop users whose overtimes are now empty
        .filter((user) => (user.overtimes?.length ?? 0) > 0);

      // Keep the day, even if users becomes empty
      return { ...day, users: filteredUsers };
    });

    console.log("after filter", filteredOvertimeDays);
    return filteredOvertimeDays;
  };

  const filterOvertimesByUser = (overtimes, userId) => {
    const isValidUserId = typeof userId === "number" && !isNaN(userId);
    return (isValidUserId
      ? overtimes.map(day => ({ ...day, users: day.users.filter(user => user.id === userId) }))
      : overtimes);
  };

  const filterOvertimesByGroup = (overtimes, selectedGroups?) => {
    return overtimes.map((day) => {
      // Filter users for the current day
      const filteredUsers = day.users.filter((user) => {

        // Filter the user's overtimes based on Group_id
        const userHasMatchingOvertime = user.overtimes.some((overtime) => {

          // const isDateMatching = overtime.date === day.startDate;
          // If no groups are selected, all overtimes are valid.
          // Otherwise, only overtimes with a group_id in selectedGroup should be considered.

          const isGroupMatching =
            selectedGroups === null || selectedGroups === undefined || parseInt(selectedGroups) === overtime.group_id;

          return isGroupMatching;
        });

        // Include the user if they have at least one matching overtime
        return userHasMatchingOvertime;
      });

      // Return a new day object with filtered users. If no users match, this day will have an empty users array.
      return {
        ...day,
        users: filteredUsers,
      };
    });
  };

  const filterOvertimesByStatus = (overtimes, selectedOTStatus) => {
    const status = selectedOTStatus === "approved" ? 1 : selectedOTStatus === "rejected" ? 2 : selectedOTStatus === "pending" ? 0 : 3;
    return overtimes.map((day) => {
      const filteredUsers = day.users.filter((user) => {

        const userHasMatchingOvertime = user.overtimes.some((overtime) => {
          // const isDateMatching = overtime.date === day.startDate;

          let isStatusMatching = true;

          if (status !== 3) {
            isStatusMatching = status === overtime.status;
          }

          // console.log("selected status", selectedOTStatus);
          // console.log("filter status", overtime.status);
          // console.log("match status", isStatusMatching);

          return isStatusMatching;

        });

        return userHasMatchingOvertime;
      });

      return {
        ...day,
        users: filteredUsers,
      };
    });
  };

  function fillOvertimesMultidays(groupFilteredOvertimeDataForMonth) {
    // Helper function to find a day object by date within the groupFilteredOvertimeDataForMonth array
    function findDayByDate(date) {
      return groupFilteredOvertimeDataForMonth.find(day => day.startDate === date);
    }

    // Helper function to check if a user exists in the day's users array
    function findUserInDay(day, userId) {
      return day.users.find(user => user.id === userId);
    }

    // Iterate over each day in the month
    groupFilteredOvertimeDataForMonth.forEach(day => {
      // For each user on this day
      day.users.forEach(user => {
        // For each overtime span of the user
        user.overtimes.forEach(overtime => {
          const fromDate = new Date(overtime.from);
          const toDate = new Date(overtime.to);

          // Iterate through every date from the start (from) to the end (to) of the overtime period
          for (let date = new Date(fromDate); date <= toDate; date.setDate(date.getDate() + 1)) {
            // Format the current date to match the "startDate" in the day objects
            const currentDate = date.toISOString().split('T')[0];

            // Find the day object for the current date
            const targetDay = findDayByDate(currentDate);

            if (targetDay) {
              // Check if the user already exists in this day
              let targetUser = findUserInDay(targetDay, user.id);

              if (!targetUser) {
                // If user doesn't exist in the day, clone the user and add to that day's users array
                targetUser = { ...user, overtimes: [overtime] };
                targetDay.users.push(targetUser);
              }

              // Check if the overtime already exists in this user's "overtimes" array
              const overtimeExists = targetUser.overtimes.some(ot => ot.id === overtime.id);

              if (!overtimeExists) {
                // If overtime doesn't exist, add it to this user's "overtimes" array
                targetUser.overtimes.push(overtime);
              }
            }
          }
        });
      });
    });
  }

  return (
    <StyledOverTimeView>
      {/* <AppTopBar /> */}
      <OverTimeToggleModal openToogle={modalOpen} anchor="right" handleCloseToggle={handleCloseToggle} timeSheetDateData={timeSheetDateData}
        span={spanInputValue?.value ?? null}
        id={userInputValue?.value ?? null}
        groupId={groupInputValue?.value ?? null}
        status={statusInputValue?.value ?? null}
        overtimeDataForMonth={overtimeDataForMonth} />
      <div className="overtime">
        <div className="overtime__title">
          <div className="overtime__title-left">
            <TitleMenuComponent text={menuTitleEnums.TITLE_OVERTIME} />
          </div>
          {/* <WrapLink onClick={() => history.push(appRouteEnums.PATH_WEEKLY_SCHEDULE)}>週間予定</WrapLink> */}
          <PassChangeButton onClick={() => handleCreateNewOT()}>
            <span style={{ display: "flex", alignItems: "center", textAlign: "center", justifyContent: "center" }}>
              <span style={{ fontWeight: "bold", fontSize: "1.5rem", paddingRight: "0.5rem" }}>＋ </span>
              新規作成
            </span>
          </PassChangeButton>

          <PassChangeButton className="overtime__title-right" onClick={() => history.push(appRouteEnums.PATH_WEEKLY_SCHEDULE)}>週間予定</PassChangeButton>
        </div>

        <div className="overtime__listItem">
          <div className="overtime__form">

            <div className="overtime__selectMonth">
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <div className="filters_label">月を選択</div>
                <input type="month" className="inputMonth" value={calendarMonth} onChange={handleCalendarMonthChange} />
              </div>
            </div>

            <div className="overtime__search">
              <div className="filters_label">日報種別</div>
              <div className="wrapperSelect">
                <AppCustomeSelect
                  placeholder="種別を選択"
                  onChange={handleOnChangeSpan}
                  options={spanOptions}
                  isClearable
                  value={spanInputValue}
                />
              </div>
            </div>

            <div className="overtime__search">
              <div className="filters_label">班・従業員</div>
              <div className="wrapperSelect">
                <AppCustomeSelect
                  placeholder="人・班を選択"
                  onChange={handleOnChangePeople}
                  groupUserOptions={peopleOptions}
                  options={peopleOptions}
                  isClearable
                  value={userInputValue?.type === "user" ? userInputValue : groupInputValue}
                />
              </div>
            </div>

            <div className="overtime__search">
              <div className="filters_label">ステータス</div>
              <div className="wrapperSelect">
                <AppCustomeSelect
                  placeholder="ステータスを選択"
                  onChange={handleOnChangeStatus}
                  groupUserOptions={statusOptions}
                  options={statusOptions}
                  isClearable
                  value={statusInputValue}
                />
              </div>
            </div>
          </div>
          {(overtimesLoading) ? (
            <div className="wrapperLoading">
              <CircularLoader loading={overtimesLoading} type="fullContent" />
            </div>
          ) : (
            <StyledWrapCalendarComponent>

              <div className="ot__calendar">
                <OverTimeCalendar handleOpenModal={handleOpenModal} handleOpenModalId={handleOpenModalId} handleCloseToggle={handleCloseToggle} dataListOT={dataListOT} month={calendarMonth} handleViewDetail={handleViewDetail} onMonthChange={handleMonthChange} />
              </div>
              <div className="csvExportDiv">
                <span className="csvExportTitle">CSVエクスポート</span>
                <div className="ot__datePicker">

                  <div className="dateContainer">

                    <div className="label">作成日時</div>

                    <div className="dateSubContainer">
                      <div className="wrapperDatePicker" style={{ display: "none" }}>
                        <input
                          type="month"
                          value={date.toISOString().slice(0, 7)} // Format as YYYY-MM
                          onChange={handleChange}
                        />
                      </div>
                      <input type="date" className="inputDate" value={cvsStartDate} onChange={(e) => setCvsStartDate(e.target.value)} /><span>から</span>
                    </div>

                    <div className="dateSubContainer">
                      <input type="date" className="inputDate" value={cvsEndDate} onChange={(e) => setCvsEndDate(e.target.value)} /><span>まで</span>
                    </div>

                  </div>
                  <div>
                    <AppExportCsv
                      key={filterKey}
                      type={"overtimes"}
                      startDate={cvsStartDate}
                      endDate={cvsEndDate}
                      title={spanInputValue?.label ? spanInputValue.label : undefined}
                      userId={userInputValue?.value ? parseInt(userInputValue.value) : undefined}
                      groupId={groupInputValue?.value ? parseInt(groupInputValue.value) : undefined}
                      status={statusInputValue?.value ? statusInputValue.value : undefined}
                    />
                  </div>
                </div>
              </div>
            </StyledWrapCalendarComponent>
          )}
        </div>
      </div>
    </StyledOverTimeView>
  );
};

// const WrapLink = styled.div`
//     font-weight: 700;
//     margin-top: 27px;

//     border: 2px solid orange;
//     color: orange;
//     border-radius: 10px;
//     display: flex;
//     align-items: center;
//     width: fit-content;
//     padding: 8px 10px;
//     :hover {
//         cursor: pointer;
//         color: #d88d02;
//         border: 2px solid #d88d02;
//     }
//     @media (min-width: ${(p) => p.theme.breakPoints.breakMedium}) {
//         margin-left: 40px;
//     }
// `;
